/**
 * 포인트(포인트) — **값과 규칙을 여기 한 곳에서만 정한다.**
 *
 * 서버와 화면이 같은 파일을 읽는다. 값을 바꾸려면 이 파일만 고치면 된다.
 * 실제로 얼마를 깎을지는 **언제나 서버가 정한다** — 화면 값은 안내용이다.
 */

/**
 * 스위치는 **서버 한 곳**이다 (`COINS_ENABLED` 환경변수, `functions/api/_lib/coins.ts`).
 * 화면은 `GET /api/coins` 로 물어서 따라간다 (`src/coins/enabled.ts`).
 * 이 파일은 서버 함수도 읽으므로 import.meta 를 쓰지 않는다.
 */

/** 포인트을 쓰는 상품 */
export type CoinProduct =
  /** 사주 전체 리포트 (12섹션 + PDF). 결과 하나당 한 번, 이후 영구 */
  | 'reportFull'
  /** 켈틱 크로스 타로. 펼칠 때마다 */
  | 'spread'
  /** 연애·배우자 궁합. 상대 한 명·관계당 한 번 */
  | 'compatLove'
  /** 일반 궁합. 상대 한 명·관계당 한 번 */
  | 'compatPlain'
  /** 같이 일할 사람 둘과 셋이 보는 궁합. 두 사람 짝 하나당 한 번 */
  | 'compatTeam'
  /** 부적. 만들 때마다 */
  | 'talisman'
  /** 이름 풀이. **이름 하나당 한 번** (같은 이름을 다시 보면 안 깎는다) */
  | 'nameReading'
  /**
   * 질문 타로 3장. **뽑을 때마다** 5P.
   *
   * 예전에는 "질문 하나당 한 번" 이었다. 그래서 같은 질문으로 새 씨앗을
   * 계속 뽑으면 값 없이 새 풀이(매번 AI)를 받을 수 있었다. 지금은 서버가 깎고,
   * **같은 판을 다시 여는 것만** 무료다 (씨앗·질문·언어가 같을 때 — `savedReading`).
   */
  | 'ask'
  /**
   * **둘 중 뭐가 나을까** — 선택지 두 개에 카드 한 장씩. 펼칠 때마다 3P.
   * 서버가 깎고, 같은 판을 다시 열면 받지 않는다 (`/api/quick` 의 `paidBefore`).
   */
  | 'compare'
  /*
   * **학업·취업운** (1-1·1-2 구성). 단위는 `src/lib/study/products.ts`.
   * 사주 여덟은 단위마다 한 번(다시 열면 0P), 타로 한 장 둘(간단 타로 안)은 뽑을 때마다. 모두 서버(`/api/study`)가 깎는다.
   * 예전 tarotExam·tarotApply·tarotPick 은 배포 전에 없앴다 (기록 0건 — 3장 질문은 `ask`, A vs B 는 `compare`).
   */
  | 'examDay'
  | 'interviewDay'
  | 'startDay'
  | 'flow12'
  | 'fitStyle'
  | 'aptitude'
  | 'envFit'
  | 'semesterFriends'
  | 'dayCard'
  | 'tiredCard'

/*
 * **"풀이 언어 바꾸기" 는 6단계에서 없앴다.** 언어가 공간으로 갈리면서 그 상품이 설
 * 자리가 사라졌다 — 다른 언어로 보고 싶으면 그 언어의 공간으로 가면 된다.
 * **장부와 잠금에 남은 옛 줄은 그대로 둔다** (돈 기록). 관리자 화면이 모르는 상품
 * 이름도 그대로 찍어 주므로 옛 줄도 읽힌다.
 */
export const COIN_PRODUCTS: readonly CoinProduct[] = [
  'reportFull',
  'spread',
  'compatLove',
  'compatPlain',
  'compatTeam',
  'talisman',
  'nameReading',
  'ask',
  'compare',
  // 학업·취업운 (사주 여덟 + 타로 한 장 둘)
  'examDay',
  'interviewDay',
  'startDay',
  'flow12',
  'fitStyle',
  'aptitude',
  'envFit',
  'semesterFriends',
  'dayCard',
  'tiredCard',
]

/** 상품값 (포인트) */
export const COIN_PRICE: Record<CoinProduct, number> = {
  /** 가격 B안 — 60P → 45P */
  reportFull: 45,
  spread: 10,
  /*
   * **궁합은 관계와 상관없이 5P**. 연애·배우자가 15P 였다.
   * 상품 이름(compatLove/compatPlain)은 그대로 둔다 — 장부·잠금의 옛 줄과 관리자 매출이 이 이름을 쓴다.
   * 글 길이는 그대로다 (연애·배우자는 두 번에 나눠 받는다).
   */
  compatLove: 5,
  compatPlain: 5,
  /** 같이 일할 사람 둘 — 한 명이면 일반 궁합(5P) 그대로 */
  compatTeam: 8,
  talisman: 5,
  nameReading: 5,
  ask: 5,
  compare: 3,
  /* 학업·취업운 (새 상품 값만 더함 — 위 값은 그대로) */
  examDay: 5,
  interviewDay: 5,
  startDay: 5,
  flow12: 10,
  fitStyle: 5,
  aptitude: 5,
  envFit: 5,
  semesterFriends: 5,
  dayCard: 1,
  tiredCard: 1,
}

/**
 * 한 번 열면 계속 열려 있는 상품. 대상마다 한 번만 깎는다.
 * 여기 없는 상품은 쓸 때마다 깎는다.
 *
 * 대상 값:
 *   reportFull                결과 번호
 *   compatLove/compatPlain    `{내 일주}|{상대 일주}|{관계}`
 *   compatTeam                `{내 일주}|{두 사람 일주}|team|p:{두 사람 번호}` (`compatTeamTarget`, 두 사람 순서와 상관없이 같은 값)
 *   talisman `{결과 번호}|{용도}|{연-월}` — 용도 하나 × 한 달 = 5P.
 *                             같은 달 안에서는 무늬 보기·저장이 계속 무료고,
 *                             다른 용도는 따로, 다음 달은 다시 받는다.
 *   nameReading               `{결과 번호}|n:{이름 해시}` — **이름 하나당 5P.**
 *                             예전에는 매번 깎는 상품이었는데, 화면이
 *                             "방금 냈다" 를 들고 있어 한 번 내면 이름을 바꿔도
 *                             계속 공짜였다. 대상에 이름을 넣어 **서버가** 가른다.
 *                             이름 원문은 보내지 않는다 — 결과 번호를 소금으로 쓴
 *                             SHA-256 앞 16자다 (`src/lib/nameTarget.ts`).
 */
export const ONCE_PER_TARGET: readonly CoinProduct[] = [
  'reportFull',
  'compatLove',
  'compatPlain',
  'compatTeam',
  'talisman',
  'nameReading',
  // 사주 여덟 — 사람 × 날짜 / 사람 × 용도 × 시작 달 / 사람 × 용도 / 사람 / 사람 × 시작 달
  'examDay',
  'interviewDay',
  'startDay',
  'flow12',
  'fitStyle',
  'aptitude',
  'envFit',
  'semesterFriends',
]

export function isOncePerTarget(product: CoinProduct): boolean {
  return (ONCE_PER_TARGET as readonly string[]).includes(product)
}

/**
 * **서버가 직접 깎는 상품.** 화면은 값을 알리고 확인만 받는다.
 *
 * 켈틱 크로스(`spread`)는 `POST /api/spread` 가 스스로 `spend()` 를 부르고
 * 못 만들면 되돌린다. 화면의 잠금 해제 길목까지 부르면 **두 번 깎인다** —
 * 매번 깎는 상품이라 서버의 "이미 열림" 짧은 길이 걸리지 않기 때문이다.
 * 예전에는 `/api/spread` 가 계정 토큰을 못 받아 늘 402 였던 탓에
 * 겉으로 드러나지 않았다.
 *
 * `ask` 도 부터 이쪽이다 — 뽑을 때마다 깎고, 같은 판을 다시 여는
 * 것만 서버가 알아보고 넘어간다.
 */
export const SERVER_SPENDS: readonly CoinProduct[] = [
  'spread',
  'compare',
  'ask',
  // 학업·취업운 열 — `/api/study` 가 깎고, 못 만들면 되돌린다
  'examDay',
  'interviewDay',
  'startDay',
  'flow12',
  'fitStyle',
  'aptitude',
  'envFit',
  'semesterFriends',
  'dayCard',
  'tiredCard',
]

export function serverSpends(product: CoinProduct): boolean {
  return (SERVER_SPENDS as readonly string[]).includes(product)
}

/** 충전 상품 */
export interface CoinPack {
  id: string
  coins: number
  krw: number
  usd: number
  /** PayPal 에서도 파는가 */
  paypal: boolean
}

/**
 * **가격 B안 — 세 묶음**. 예전 넷(30P·70P·150P·330P, c30~c330)은 없앴다.
 * 새 주문은 `packById` 가 옛 id 를 못 찾아 만들어지지 않는다 (`charge.ts`). **이미 만들어진 옛 주문**
 * (검사·시험, 모두 pending)은 `confirm.ts` 가 주문에 저장된 금액과만 대조하므로 코드로는 막히지 않는다 —
 * 보고에 적었다 (DB·서버는 이번에 건드리지 않았다).
 */
export const COIN_PACKS: readonly CoinPack[] = [
  { id: 'c50', coins: 50, krw: 4900, usd: 3.99, paypal: true },
  { id: 'c110', coins: 110, krw: 9900, usd: 7.99, paypal: true },
  { id: 'c240', coins: 240, krw: 19900, usd: 15.99, paypal: true },
]

export function packById(id: string): CoinPack | undefined {
  return COIN_PACKS.find((p) => p.id === id)
}

/** 가입 선물 (계정당 한 번) */
export const SIGNUP_BONUS = 10

/**
 * **매일 출석** — 로그인한 사람이 하루 한 번 받는 선물 포인트.
 *
 * "하루" 는 **서버의 UTC 날짜**다 (마이그레이션 0034). 기기 시간대를 따르면 시계를
 * 옮겨 하루에 여러 번 받을 수 있다. 값과 판정 모두 서버가 정하고, 화면은 보여 주기만 한다.
 */
export const DAILY_CHECKIN = 1

/** 유효기간 (일) */
export const EXPIRY_DAYS = {
  /** 산 포인트 5년 */
  paid: 365 * 5,
  /** 보너스·선물 포인트 1년 */
  bonus: 365,
} as const

/** 결제 수단. PayPal 은 계약 전이라 화면에서 숨기되 칸은 둔다. */
export const PAY_METHODS = ['toss', 'paypal'] as const
export type PayMethod = (typeof PAY_METHODS)[number]

/** 지금 실제로 쓸 수 있는 결제 수단 */
export const ACTIVE_PAY_METHODS: readonly PayMethod[] = ['toss']

/** 한국어 화면은 원화, 나머지는 원화로 결제하고 달러를 함께 보여 준다. */
export function showsUsd(locale: string): boolean {
  return locale !== 'ko'
}

/**
 * **메뉴 카드 구석에 붙는 값 표시**.
 *
 * 예전에는 누르기 전에는 값을 알 수 없었고, 누르면 갑자기 값 이야기가 나왔다.
 * 들어가기 전에 무료인지 얼마인지 보이면 마음이 편하다.
 *
 * 값이 없는 칸(오늘의 운세·동물 카드·사용설명서)은 **무료**다.
 * 한 묶음에 값이 여럿이면 **가장 싼 것**을 적는다 (궁합은 하나로 5P) —
 * "여기서 쓰려면 적어도 이만큼" 이 사람이 알고 싶은 값이다.
 */
export const GROUP_PRICE: Record<string, number> = {
  saju: COIN_PRICE.reportFull,
  // 간단 타로에 한 장(1P)이 들어왔다 — 가장 싼 것이 1P
  tarot: Math.min(COIN_PRICE.spread, COIN_PRICE.ask, COIN_PRICE.compare, COIN_PRICE.dayCard, COIN_PRICE.tiredCard),
  compat: Math.min(COIN_PRICE.compatLove, COIN_PRICE.compatPlain),
  charm: COIN_PRICE.talisman,
  name: COIN_PRICE.nameReading,
  // 공부·취업 묶음 — 사주 여덟만 (타로는 간단 타로로 옮겼다). 가장 싼 것 5P
  study: Math.min(
    COIN_PRICE.examDay,
    COIN_PRICE.interviewDay,
    COIN_PRICE.startDay,
    COIN_PRICE.flow12,
    COIN_PRICE.fitStyle,
    COIN_PRICE.aptitude,
    COIN_PRICE.envFit,
    COIN_PRICE.semesterFriends,
  ),
  // 타로 묶음에는 무료(YES or NO)가 들어 있지만, 한 묶음에 무료와 유료가 섞이면
  // **유료**로 적는다. 값은 가장 싼 것으로 — 여기서는 3P 다.
  ask: Math.min(COIN_PRICE.ask, COIN_PRICE.compare, COIN_PRICE.dayCard, COIN_PRICE.tiredCard),
}

/** 그 메뉴에 드는 값. 0 이면 무료다. */
export function groupPrice(group: string): number {
  return GROUP_PRICE[group] ?? 0
}

/**
 * **관리자가 한 번에 주거나 뺄 수 있는 최대**.
 * 0을 하나 더 붙이는 실수를 막는다. 그만큼 주려면 나눠서 여러 번 한다.
 * 화면도 막지만 **판정은 서버**다 (`functions/api/admin/coins.ts`).
 */
export const ADMIN_MAX_COINS = 10_000
